import React from 'react';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Slider,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { Download } from '@mui/icons-material';

// --- helpers ---------------------------------------------------
function splitItems(str = '') {
  return String(str)
    .split(';')
    .map(s => s.trim())
    .filter(Boolean);
}
function hexToRgb(hex) {
  const h = hex.replace('#', '');
  const bigint = parseInt(h.length === 3 ? h.split('').map(c => c + c).join('') : h, 16);
  return { r: (bigint >> 16) & 255, g: (bigint >> 8) & 255, b: bigint & 255 };
}
function rgbToHex(r, g, b) {
  const x = (n) => n.toString(16).padStart(2, '0');
  return `#${x(r)}${x(g)}${x(b)}`;
}
function mix(hex, otherHex = '#ffffff', amount = 0.08) {
  try {
    const a = hexToRgb(hex);
    const b = hexToRgb(otherHex);
    const r = Math.round(a.r * (1 - amount) + b.r * amount);
    const g = Math.round(a.g * (1 - amount) + b.g * amount);
    const bl = Math.round(a.b * (1 - amount) + b.b * amount);
    return rgbToHex(r, g, bl);
  } catch {
    return hex;
  }
}

const FONT_CHOICES = [
  'Inter, Roboto, Arial, sans-serif',
  'Roboto, Arial, sans-serif',
  'Arial, Helvetica, sans-serif',
  '"Segoe UI", Tahoma, Geneva, Verdana, sans-serif',
  '"Helvetica Neue", Helvetica, Arial, sans-serif',
  'Georgia, "Times New Roman", serif',
  '"Times New Roman", Times, serif',
  '"Courier New", Courier, monospace',
  'Verdana, Geneva, Tahoma, sans-serif',
];

const ASPECT_CHOICES = [
  { key: '16:9',  w:16, h:9  },
  { key: '9:16',  w:9,  h:16 },
  { key: '4:3',   w:4,  h:3  },
  { key: '3:4',   w:3,  h:4  },
  { key: '2:3',   w:2,  h:3  },
  { key: '3:2',   w:3,  h:2  },
  { key: '1:1',   w:1,  h:1  },
  { key: '5:4',   w:5,  h:4  },
  { key: '4:5',   w:4,  h:5  },
];

// --- component -------------------------------------------------
class FloorDirectoryImageCreator extends React.Component {
  canvasRef = React.createRef();

  state = {
    title: 'Floor Directory',
    fontFamily: FONT_CHOICES[0],
    textColor: '#193A8A',
    backgroundColor: '#ffffff',
    columns: 2,
    aspectKey: '16:9',
    baseWidth: 1920, // HD width for signage

    // sizes
    labelScalePct: 40,
    infoScalePct: 24,
    labelInfoGap: 0,

    // label width
    labelWidthMode: 'auto', // 'auto' | 'manual'
    labelColPct: 20,

    // row separation options: 'alt' | 'line-thin' | 'line-thick'
    rowSeparation: 'alt',

    // vertical alignment: 'top' | 'center' | 'bottom'
    vAlign: 'top',
  };

  componentDidMount() { this.draw(); }
  componentDidUpdate(prevProps, prevState) {
    if (prevProps.floors !== this.props.floors || JSON.stringify(prevState) !== JSON.stringify(this.state)) {
      this.draw();
    }
  }

  getFilteredFloors() {
    const floors = Array.isArray(this.props.floors) ? this.props.floors : [];
    return floors
      .filter(f => (f.floor_info || '').trim().length > 0)
      .map(f => ({ label: f.floor_label || '', items: splitItems(f.floor_info || '') }))
      .reverse(); // highest floors first
  }

  getAspectDims() {
    const { baseWidth, aspectKey } = this.state;
    const a = ASPECT_CHOICES.find(x => x.key === aspectKey) || ASPECT_CHOICES[0];
    return { width: baseWidth, height: Math.round(baseWidth * (a.h / a.w)) };
  }

  draw() {
    const items = this.getFilteredFloors();
    const { width: W, height: H } = this.getAspectDims();
    const {
      title, fontFamily, textColor, backgroundColor, columns,
      labelScalePct, infoScalePct, labelInfoGap,
      labelWidthMode, labelColPct, rowSeparation, vAlign
    } = this.state;

    const canvas = this.canvasRef.current;
    if (!canvas) return;
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext('2d');

    // layout
    const padding = Math.max(24, Math.round(W * 0.02));
    const headerH = Math.max(80, Math.min(160, Math.round(H * 0.10)));
    const innerW = W - padding * 2;
    const listTop = padding + headerH + Math.max(16, Math.round(H * 0.015));
    const listH = H - listTop - padding;

    const cols = Math.max(1, Math.min(3, Number(columns) || 1));
    const colGap = Math.max(16, Math.round(innerW * 0.02));
    const colW = Math.floor((innerW - colGap * (cols - 1)) / cols);

    // fonts
    const refRowsPerCol = Math.max(1, Math.ceil(items.length / cols));
    const refRowH = Math.max(56, Math.min(180, Math.floor(listH / refRowsPerCol)));
    const labelFontPx = Math.max(18, Math.round(refRowH * (labelScalePct / 100)));
    const itemFontPx  = Math.max(12, Math.round(refRowH * (infoScalePct / 100)));
    const lineGap     = Math.round(itemFontPx * 0.35);
    const labelFont   = `700 ${labelFontPx}px ${fontFamily}`;
    const itemFont    = `500 ${itemFontPx}px ${fontFamily}`;

    const cellPadX = Math.max(8, Math.round(colW * 0.02));
    const cellPadY = Math.max(6, Math.round(itemFontPx * 0.55));

    const altRow = mix(backgroundColor, '#000000', 0.04);
    const borderColor = mix(textColor, backgroundColor, 0.75);

    // bg
    ctx.fillStyle = backgroundColor;
    ctx.fillRect(0, 0, W, H);

    // title
    ctx.fillStyle = textColor;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.font = `bold ${Math.max(28, Math.round(headerH * 0.45))}px ${fontFamily}`;
    ctx.fillText(title || '', padding, padding + headerH / 2);

    // divider under title
    ctx.strokeStyle = mix(textColor, backgroundColor, 0.8);
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(padding, padding + headerH);
    ctx.lineTo(W - padding, padding + headerH);
    ctx.stroke();

    // label column width
    ctx.font = labelFont;
    const measuredMax = Math.ceil(Math.max(...items.map(it => ctx.measureText(it.label || '').width), 0));
    const manualWidth = Math.round(colW * (labelColPct / 100));
    const autoWidth   = Math.min(Math.max(measuredMax + 4, 40), Math.round(colW * 0.6));
    const labelColW   = (labelWidthMode === 'manual')
      ? Math.max(manualWidth, measuredMax + 4)
      : autoWidth;

    // row height
    const computeItemHeight = (it) => {
      const lines = Math.max(1, it.items.length);
      const infoBlock = itemFontPx * lines + lineGap * (lines - 1);
      const innerHeight = Math.max(labelFontPx, infoBlock);
      return cellPadY * 2 + innerHeight;
    };

    ctx.textAlign = 'left';
    ctx.textBaseline = 'alphabetic';

    let idx = 0;
    for (let c = 0; c < cols; c++) {
      const x0 = padding + c * (colW + colGap);
      let y = listTop;

      // col separator
      if (c > 0) {
        ctx.strokeStyle = borderColor;
        ctx.beginPath();
        ctx.moveTo(x0 - Math.floor(colGap / 2), listTop - 8);
        ctx.lineTo(x0 - Math.floor(colGap / 2), listTop + listH + 8);
        ctx.stroke();
      }

      let localRow = 0;
      while (idx < items.length) {
        const it = items[idx];
        const rowH = computeItemHeight(it);
        const nextBottom = y + rowH;
        if (nextBottom > listTop + listH && y > listTop) break;

        // row separation
        if (rowSeparation === 'alt') {
          if ((localRow + c) % 2 === 0) {
            ctx.fillStyle = altRow;
            ctx.fillRect(x0, y, colW, rowH);
          }
        } else if ((rowSeparation === 'line-thin' || rowSeparation === 'line-thick') && y > listTop) {
          ctx.strokeStyle = mix(textColor, backgroundColor, 0.85);
          ctx.lineWidth = rowSeparation === 'line-thick' ? 3 : 1;
          ctx.beginPath();
          ctx.moveTo(x0, y);
          ctx.lineTo(x0 + colW, y);
          ctx.stroke();
        }

        // inner vertical alignment
        const lines = Math.max(1, it.items.length);
        const infoBlockH = itemFontPx * lines + lineGap * (lines - 1);
        const innerH = Math.max(labelFontPx, infoBlockH);
        const alignOffset = (blockH) =>
          vAlign === 'center' ? (innerH - blockH) / 2 :
          vAlign === 'bottom' ? (innerH - blockH) : 0;

        // label
        ctx.fillStyle = textColor;
        ctx.font = labelFont;
        const labelTop = y + cellPadY + alignOffset(labelFontPx);
        ctx.fillText(it.label || '', x0 + cellPadX, labelTop + labelFontPx);

        // info
        ctx.font = itemFont;
        const infoTop = y + cellPadY + alignOffset(infoBlockH);
        const infoX = x0 + cellPadX + labelColW + Math.max(0, Number(labelInfoGap) || 0);
        let lineY = infoTop + itemFontPx;
        for (let k = 0; k < it.items.length; k++) {
          ctx.fillText(it.items[k], infoX, lineY);
          lineY += itemFontPx + lineGap;
        }

        y = nextBottom + Math.max(6, Math.round(H * 0.005));
        idx++; localRow++;
        if (y >= listTop + listH) break;
      }
      if (idx >= items.length) break;
    }

    // outer border
    ctx.strokeStyle = borderColor;
    ctx.lineWidth = 1;
    ctx.strokeRect(0.5, 0.5, W - 1, H - 1);
  }

  download = () => {
    const { aspectKey } = this.state;
    const filename = `floor_directory_${aspectKey.replace(':', 'x')}.jpg`;
    const link = document.createElement('a');
    link.download = filename;
    link.href = this.canvasRef.current.toDataURL('image/jpeg', 0.95); // high quality JPEG
    link.click();
  };

  render() {
    const items = this.getFilteredFloors();
    const {
      title, fontFamily, textColor, backgroundColor, columns, aspectKey,
      labelScalePct, infoScalePct, labelInfoGap, labelWidthMode, labelColPct,
      rowSeparation, vAlign
    } = this.state;

    return (
      <Box>
        <Stack spacing={2} sx={{ mb: 2 }}>
          <TextField
            label="Title"
            value={title}
            onChange={(e) => this.setState({ title: e.target.value })}
            fullWidth
          />

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <FormControl fullWidth>
              <InputLabel>Font</InputLabel>
              <Select
                label="Font"
                value={fontFamily}
                onChange={(e) => this.setState({ fontFamily: e.target.value })}
              >
                {FONT_CHOICES.map(f => (
                  <MenuItem key={f} value={f} sx={{ fontFamily: f }}>
                    {f.split(',')[0]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField label="Text color" type="color" value={textColor}
              onChange={(e) => this.setState({ textColor: e.target.value })}
              InputLabelProps={{ shrink: true }} sx={{ minWidth: 160 }} />
            <TextField label="Background color" type="color" value={backgroundColor}
              onChange={(e) => this.setState({ backgroundColor: e.target.value })}
              InputLabelProps={{ shrink: true }} sx={{ minWidth: 160 }} />
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <FormControl sx={{ minWidth: 160 }}>
              <InputLabel>Columns</InputLabel>
              <Select
                label="Columns"
                value={columns}
                onChange={(e) => this.setState({ columns: Number(e.target.value) })}
              >
                <MenuItem value={1}>1</MenuItem>
                <MenuItem value={2}>2</MenuItem>
                <MenuItem value={3}>3</MenuItem>
              </Select>
            </FormControl>
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel>Image format</InputLabel>
              <Select
                label="Image format"
                value={aspectKey}
                onChange={(e) => this.setState({ aspectKey: e.target.value })}
              >
                {ASPECT_CHOICES.map(a => (
                  <MenuItem key={a.key} value={a.key}>{a.key}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          {/* Size & spacing */}
          <Stack spacing={1}>
            <Typography variant="body2">Floor number size</Typography>
            <Slider value={labelScalePct} onChange={(_, v) => this.setState({ labelScalePct: v })}
              valueLabelDisplay="auto" min={10} max={80} step={1}/>
            <Typography variant="body2">Floor information size</Typography>
            <Slider value={infoScalePct} onChange={(_, v) => this.setState({ infoScalePct: v })}
              valueLabelDisplay="auto" min={8} max={80} step={1}/>
            <Typography variant="body2">Gap between number & info (px)</Typography>
            <Slider value={labelInfoGap} onChange={(_, v) => this.setState({ labelInfoGap: v })}
              valueLabelDisplay="auto" min={0} max={60} step={1}/>
          </Stack>

          {/* Label width */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <FormControl sx={{ minWidth: 160 }}>
              <InputLabel>Label width mode</InputLabel>
              <Select
                label="Label width mode"
                value={labelWidthMode}
                onChange={(e) => this.setState({ labelWidthMode: e.target.value })}
              >
                <MenuItem value="auto">Auto (tight)</MenuItem>
                <MenuItem value="manual">Manual (% of col)</MenuItem>
              </Select>
            </FormControl>
            {labelWidthMode === 'manual' && (
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2">Label column %</Typography>
                <Slider value={labelColPct} onChange={(_, v) => this.setState({ labelColPct: v })}
                  valueLabelDisplay="auto" min={5} max={70} step={1}/>
              </Box>
            )}
          </Stack>

          {/* Separation + alignment */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel>Row separation</InputLabel>
              <Select
                label="Row separation"
                value={rowSeparation}
                onChange={(e) => this.setState({ rowSeparation: e.target.value })}
              >
                <MenuItem value="alt">Alternate background</MenuItem>
                <MenuItem value="line-thin">Divider (thin)</MenuItem>
                <MenuItem value="line-thick">Divider (thick)</MenuItem>
              </Select>
            </FormControl>

            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel>Vertical alignment</InputLabel>
              <Select
                label="Vertical alignment"
                value={vAlign}
                onChange={(e) => this.setState({ vAlign: e.target.value })}
              >
                <MenuItem value="top">Top</MenuItem>
                <MenuItem value="center">Center</MenuItem>
                <MenuItem value="bottom">Bottom</MenuItem>
              </Select>
            </FormControl>
          </Stack>

          <Typography variant="body2" color="text.secondary">
            {items.length} entries included (highest floors on top).
          </Typography>
        </Stack>

        <Box sx={{ display: 'flex', justifyContent: 'center', p: 1, bgcolor: 'background.paper' }}>
          <canvas ref={this.canvasRef} style={{ maxWidth: '100%', borderRadius: 8, border: '1px solid #eee' }} />
        </Box>

        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
          <Button onClick={this.download} variant="contained" startIcon={<Download />}>
            Download JPEG (HD)
          </Button>
        </Stack>
      </Box>
    );
  }
}

export default FloorDirectoryImageCreator;
